import React from 'react';
import { createRoot } from 'react-dom/client';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import App from './App';
import { ErrorBoundary } from './components/ErrorBoundary';
import { DebugLogPanel } from './components/DebugLogPanel';
import { APP_VERSION } from '@shared/utils/constants';
import { addinLogger, isDebugMode, setDebugMode } from './utils/addinLogger';
import './index.css';

addinLogger.info('Add-in script started', `debug=${isDebugMode()}`);

const LoadingMessage: React.FC<{ message: string }> = ({ message }) => {
  const enableDebug = () => {
    setDebugMode(true);
    window.location.reload();
  };
  return (
    <div style={{ padding: 20, fontFamily: 'sans-serif' }}>
      <p style={{ margin: 0 }}>{message}</p>
      <p style={{ marginTop: 8, fontSize: 12, color: '#666' }}>v{APP_VERSION}</p>
      {!isDebugMode() && (
        <p style={{ marginTop: 12, fontSize: 11 }}>
          <button
            type="button"
            onClick={enableDebug}
            style={{ background: 'none', border: 'none', color: '#0078d4', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
          >
            Enable debug log
          </button>
          {' — show logs in pane for troubleshooting'}
        </p>
      )}
    </div>
  );
};

const NotOutlookFallback: React.FC<{ hostDisplay: string }> = ({ hostDisplay }) => (
  <div style={{ padding: 20, fontFamily: 'sans-serif' }}>
    <p>This add-in is designed for Outlook.</p>
    <p style={{ fontSize: 12, color: '#666' }}>Detected host: {hostDisplay}</p>
    <p style={{ fontSize: 12, color: '#666' }}>v{APP_VERSION}</p>
  </div>
);

function withDebugPanel(node: React.ReactNode) {
  if (!isDebugMode()) return node;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ flex: 1, overflow: 'auto' }}>{node}</div>
      <DebugLogPanel />
    </div>
  );
}

let container = document.getElementById('root');
if (!container) {
  const div = document.createElement('div');
  div.id = 'root';
  document.body.appendChild(div);
  container = div;
}

const root = createRoot(container);

// Loading view: plain HTML only (no Fluent). FluentProvider at root would crash with "useContext of null".
function loadingView(message: string) {
  return withDebugPanel(<LoadingMessage message={message} />);
}

// Single loading state so the task pane is never blank (avoids duplicate "loading" text)
root.render(loadingView('Loading Athena Outlook Assistant...'));

let timeoutId: ReturnType<typeof setTimeout> | null = setTimeout(() => {
  timeoutId = null;
  addinLogger.warn('Office.onReady did not fire within 10s');
  root.render(loadingView('Taking longer than expected. Open Developer Tools (F12) or enable debug log below.'));
}, 10000);

/** Minimal fallback when the whole app tree crashes (no Fluent, so it never throws). */
function CrashFallback() {
  const enableDebug = () => {
    setDebugMode(true);
    window.location.reload();
  };
  return (
    <div style={{ padding: 20, fontFamily: 'sans-serif' }}>
      <p style={{ margin: 0, fontWeight: 600, color: '#c42b1c' }}>Something went wrong during load</p>
      <p style={{ margin: '8px 0', fontSize: 14, color: '#333' }}>
        The add-in crashed after opening. Enable debug log to see the error, or open Developer Tools (F12) and check the Console.
      </p>
      <button
        type="button"
        onClick={enableDebug}
        style={{ background: 'none', border: 'none', color: '#0078d4', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: 14 }}
      >
        Enable debug log
      </button>
      <p style={{ marginTop: 12, fontSize: 12, color: '#666' }}>v{APP_VERSION}</p>
    </div>
  );
}

function renderApp() {
  // StrictMode removed: it caused Fluent UI (Text/useContext) to see null context and throw.
  // FluentProvider is applied once at the root in Office.onReady (so it is the first React node).
  return (
    <ErrorBoundary fallback={<CrashFallback />}>
      <ErrorBoundary minimalFallback>
        <App />
      </ErrorBoundary>
    </ErrorBoundary>
  );
}

// Initialize Office.js and then show the app or a fallback
Office.onReady((info) => {
  const hostName = info.host != null ? String(Office.HostType[info.host] ?? info.host) : 'unknown';
  addinLogger.info('Office.onReady called', `host=${hostName}`);
  addinLogger.logIdentityDiagnostics();

  if (timeoutId) {
    clearTimeout(timeoutId);
    timeoutId = null;
  }

  if (info.host === Office.HostType.Outlook) {
    addinLogger.info('Rendering App (Outlook)');
    root.render(
      <FluentProvider theme={webLightTheme}>
        {withDebugPanel(renderApp())}
      </FluentProvider>
    );
  } else {
    addinLogger.warn('Not Outlook host, showing fallback', hostName);
    root.render(
      <FluentProvider theme={webLightTheme}>
        {withDebugPanel(<NotOutlookFallback hostDisplay={hostName} />)}
      </FluentProvider>
    );
  }
});





